import React from 'react';
import PropTypes from 'prop-types';
import gettext from 'js-utils/gettext';
import EBPropTypes from 'js-utils/prop-types';
import { ReCaptcha } from 'react-recaptcha-v3';

import FormSubmitButton from '../FormSubmitButton';
import PasswordField from '../PasswordField';
import StaticEmailField from '../StaticEmailField';
import CaptchaField from '../CaptchaField';

import Button from 'eventbrite_design_system/button/Button';
import { formValidate } from 'eventbrite_design_system/utils/form';
import {
    TYPE_BUTTON,
    STYLE_ANCHOR,
} from 'eventbrite_design_system/button/constants';

import { logInValidate as validate } from '../utils';
import { LOG_IN_FORM } from '../../constants';

const CAPTCHA_POLL_RATE = 100;
const CAPTCHA_RETRY_RATE = 110000;
const CAPTCHA_MAX_WAIT_TIME = 5000;

const ForgotPasswordLink = ({ handleForgotPassword }) => (
    <div className="eds-l-pad-top-2 eds-text--center">
        <Button
            type={TYPE_BUTTON}
            style={STYLE_ANCHOR}
            onClick={handleForgotPassword}
        >
            {gettext('Forgot password')}
        </Button>
    </div>
);

class LogInForm extends React.PureComponent {
    static propTypes = {
        handleSubmit: PropTypes.func.isRequired,
        email: PropTypes.string,
        handleEmailEdit: PropTypes.func,
        buttonCTA: EBPropTypes.translation,
        handleForgotPassword: PropTypes.func,
        showNotification: PropTypes.func,
        clearNotification: PropTypes.func,
        recaptcha2PublicKey: PropTypes.string,
        recaptcha3PublicKey: PropTypes.string,
        showCaptcha: PropTypes.bool,

        /* Props Provided Via ReduxForm */
        submitFailed: PropTypes.bool,
        submitSucceeded: PropTypes.bool,
        submitting: PropTypes.bool,
    };

    state = {
        enableCaptchaV3: false,
        captchaToken: null,
    };

    componentWillReceiveProps({ submitting, submitFailed, error }) {
        const { showNotification, clearNotification } = this.props;

        /*If the form has an error and submit failed, attempt
        to display a notification*/
        if (submitFailed && error) {
            showNotification(error);
        }

        /*We want to limit only one error notification to
        appear per form submission if the user has dismissed it.
        So each time submitting is true, then reset the flag.*/
        if (submitting) {
            clearNotification();
        }
    }

    componentWillUnmount() {
        const { clearNotification } = this.props;

        clearNotification();
    }

    _handleForgotPassword() {
        const { handleForgotPassword, clearNotification } = this.props;

        clearNotification();
        handleForgotPassword();
    }

    _handleDelayedSubmit = (event) => {
        event.preventDefault();

        this.setState({
            enableCaptchaV3: true,
        });

        this.captchaStartTime = Date.now();

        const submitIfCaptcha = () => {
            const currentTime = Date.now();

            if (
                this.state.captchaToken ||
                this.captchaStartTime + CAPTCHA_MAX_WAIT_TIME < currentTime ||
                this.props.disableRecaptcha3
            ) {
                this.props.handleSubmit();
            } else {
                setTimeout(submitIfCaptcha, CAPTCHA_POLL_RATE);
            }
        };

        submitIfCaptcha();
    };

    _handleCaptchaV3 = (captchaToken) => {
        this.setState({
            enableCaptchaV3: false,
            captchaToken,
        });

        this.props.change('recaptchaV3ResponseToken', captchaToken);

        //The recaptcha token expires after two minutes
        setTimeout(() => {
            this.setState({ enableCaptchaV3: true });
        }, CAPTCHA_RETRY_RATE);
    };

    _handlePasswordInput = () => {
        this.setState({
            enableCaptchaV3: true,
        });
    };

    render() {
        const {
            email,
            handleEmailEdit,
            submitting,
            submitSucceeded,
            buttonCTA,
            clearNotification,
            submitFailed,
            showCaptcha,
            recaptcha2PublicKey,
            recaptcha3PublicKey,
            disableRecaptcha3,
        } = this.props;

        const { enableCaptchaV3 } = this.state;

        const buttonClasses =
            'eds-fx--fade-in-down eds-fx--delay-1 eds-l-mar-top-6';

        const isSubmitDisabled = submitting || submitSucceeded;

        return (
            <div className="eds-fx--fade-in eds-fx--delay-2">
                <form
                    onSubmit={this._handleDelayedSubmit}
                    noValidate="novalidate"
                >
                    <StaticEmailField
                        email={email}
                        onEmailEdit={handleEmailEdit}
                        clearNotification={clearNotification}
                    />
                    <PasswordField
                        isLogin={true}
                        submitFailed={submitFailed}
                        onChange={this._handlePasswordInput}
                    />
                    <CaptchaField
                        showCaptcha={showCaptcha}
                        recaptcha2PublicKey={recaptcha2PublicKey}
                        submitFailed={submitFailed}
                    />

                    {enableCaptchaV3 && !disableRecaptcha3 && (
                        <ReCaptcha
                            sitekey={recaptcha3PublicKey}
                            action="login"
                            verifyCallback={this._handleCaptchaV3.bind(this)}
                        />
                    )}

                    <FormSubmitButton
                        submitDisabled={isSubmitDisabled}
                        buttonClasses={buttonClasses}
                        buttonCTA={buttonCTA}
                    />
                    <ForgotPasswordLink
                        handleForgotPassword={this._handleForgotPassword.bind(
                            this,
                        )}
                    />
                </form>
            </div>
        );
    }
}

export default formValidate({
    validate,
    form: LOG_IN_FORM,
    touchOnBlur: false,
})(LogInForm);
